/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import { DEFAULT_CURRENCY_SYMBOL as currencySymbol, STORAGE_KEYS } from '../../constants';

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value || 0));

const ViewShell = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
}));

const HeroStrip = styled(Box)(({ theme }) => ({
  borderRadius: 6,
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha('#FEF2F2', 0.95)} 0%, ${alpha('#FFFFFF', 1)} 100%)`,
  border: `1px solid ${alpha(theme.palette.error.main, 0.14)}`,
}));

const MetricTile = styled(Box)(({ theme }) => ({
  borderRadius: 6,
  padding: theme.spacing(1.7),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
  height: '100%',
}));

const FilterBar = styled(Box)(({ theme }) => ({
  borderRadius: 6,
  padding: theme.spacing(1.6),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
}));

const FeeRow = styled(Card)(({ theme }) => ({
  borderRadius: 6,
  border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
  boxShadow: '0 12px 26px rgba(15, 23, 42, 0.04)',
}));

const StatusChip = ({ label, tone }) => {
  const colorMap = {
    overdue: { bg: alpha('#EF4444', 0.12), color: '#B91C1C' },
    soon: { bg: alpha('#F59E0B', 0.16), color: '#B45309' },
    pending: { bg: alpha('#64748B', 0.12), color: '#475569' },
  };
  const style = colorMap[tone] || colorMap.pending;
  return (
    <Chip
      size="small"
      label={label}
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700 }}
    />
  );
};

const PendingFeesComponent = () => {
  const [pendingFees, setPendingFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [classFilter, setClassFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [classes, setClasses] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedFee, setSelectedFee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const schoolId = localStorage.getItem(STORAGE_KEYS.SCHOOL_ID);

  const classifyUrgency = (fee) => {
    const dueDateValue = fee.dueDate || fee.lastDueDate || fee.nextDueDate;
    if (!dueDateValue) {
      return { tone: 'pending', label: 'Pending', overdueDays: 0 };
    }

    const dueDate = new Date(dueDateValue);
    const today = new Date();
    const dueOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = Math.floor((dueOnly - todayOnly) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { tone: 'overdue', label: `Overdue ${Math.abs(diffDays)}d`, overdueDays: Math.abs(diffDays) };
    }
    if (diffDays <= 7) {
      return { tone: 'soon', label: `Due in ${diffDays}d`, overdueDays: 0 };
    }
    return { tone: 'pending', label: 'Upcoming', overdueDays: 0 };
  };

  const fetchPendingFees = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/fees/pending-fees', {
        headers: { schoolId },
      });
      setPendingFees(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Error fetching pending fees.'));
      setPendingFees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingFees();
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    Promise.all([
      axiosInstance.get('/api/classes', { headers: { schoolId } }),
      axiosInstance.get('/api/sections', { headers: { schoolId } }),
    ])
      .then(([classesRes, sectionsRes]) => {
        setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
        setAllSections(Array.isArray(sectionsRes.data) ? sectionsRes.data : []);
      })
      .catch((err) => {
        setClasses([]);
        setAllSections([]);
        setError(getApiErrorMessage(err, 'Failed to load class filters.'));
      });
  }, [schoolId]);

  useEffect(() => {
    if (!classFilter) {
      setSections([]);
      return;
    }
    const selectedClass = classes.find((cls) => String(cls.classId) === String(classFilter));
    const assignedSectionIds = String(selectedClass?.assignedSections || '')
      .split(',')
      .map((value) => Number(String(value).trim()))
      .filter((value) => !Number.isNaN(value));

    const availableSections = allSections.filter((section) => assignedSectionIds.includes(Number(section.sectionId)));
    setSections(availableSections);
    if (!availableSections.some((section) => String(section.sectionId) === String(sectionFilter))) {
      setSectionFilter('');
    }
  }, [allSections, classFilter, classes, sectionFilter]);

  const enrichedFees = useMemo(
    () =>
      pendingFees.map((fee) => ({
        ...fee,
        urgency: classifyUrgency(fee),
      })),
    [pendingFees]
  );

  const filteredFees = useMemo(() => {
    return enrichedFees
      .filter((fee) => (classFilter === '' ? true : String(fee.classId || fee.className || '') === String(classFilter)))
      .filter((fee) => (sectionFilter === '' ? true : String(fee.sectionId || fee.sectionName || '') === String(sectionFilter)))
      .filter((fee) => {
        if (urgencyFilter === 'all') return true;
        return fee.urgency.tone === urgencyFilter;
      })
      .sort((a, b) => {
        const toneOrder = { overdue: 0, soon: 1, pending: 2 };
        const urgencyDiff = toneOrder[a.urgency.tone] - toneOrder[b.urgency.tone];
        if (urgencyDiff !== 0) return urgencyDiff;
        return Number(b.pendingAmount || 0) - Number(a.pendingAmount || 0);
      });
  }, [classFilter, enrichedFees, sectionFilter, urgencyFilter]);

  const totalPending = useMemo(
    () => filteredFees.reduce((sum, fee) => sum + Number(fee.pendingAmount || 0), 0),
    [filteredFees]
  );
  const overdueCount = useMemo(() => filteredFees.filter((fee) => fee.urgency.tone === 'overdue').length, [filteredFees]);
  const dueSoonCount = useMemo(() => filteredFees.filter((fee) => fee.urgency.tone === 'soon').length, [filteredFees]);
  const studentCount = filteredFees.length;

  const openTransactions = (fee) => {
    setSelectedFee(fee);
    setDialogOpen(true);
  };

  const buildExportRows = () =>
    filteredFees.map((fee) => ({
      studentName: `${fee.firstName || ''} ${fee.lastName || ''}`.trim() || 'Unnamed Student',
      fatherName: fee.fatherName || 'N/A',
      className: fee.className || 'N/A',
      sectionName: fee.sectionName || 'N/A',
      totalFee: Number(fee.totalFee || 0),
      paidAmount: Number(fee.paidAmount || 0),
      pendingAmount: Number(fee.pendingAmount || 0),
      dueDate: fee.dueDate || fee.lastDueDate || fee.nextDueDate || 'Not available',
      urgency: fee.urgency.label,
    }));

  const exportBaseName = useMemo(() => {
    const className = classes.find((cls) => String(cls.classId) === String(classFilter))?.className;
    const sectionName = sections.find((sec) => String(sec.sectionId) === String(sectionFilter))?.sectionName;
    const scope = [
      'pending_fees',
      className ? `class_${className}` : 'all_classes',
      sectionName ? `section_${sectionName}` : 'all_sections',
      urgencyFilter !== 'all' ? urgencyFilter : 'all',
      new Date().toISOString().split('T')[0],
    ]
      .join('_')
      .replace(/[^a-zA-Z0-9_-]+/g, '_');
    return scope;
  }, [classFilter, classes, sectionFilter, sections, urgencyFilter]);

  const exportContextLabel = useMemo(() => {
    const className = classes.find((cls) => String(cls.classId) === String(classFilter))?.className;
    const sectionName = sections.find((sec) => String(sec.sectionId) === String(sectionFilter))?.sectionName;
    const priorityLabel =
      urgencyFilter === 'all'
        ? 'All Pending'
        : urgencyFilter === 'overdue'
          ? 'Overdue Only'
          : urgencyFilter === 'soon'
            ? 'Due Soon'
            : 'Upcoming';

    if (!className && !sectionName && urgencyFilter === 'all') {
      return 'All Pending Fees';
    }

    return [
      className ? `Class: ${className}` : 'All Classes',
      sectionName ? `Section: ${sectionName}` : 'All Sections',
      `Priority: ${priorityLabel}`,
    ].join(' | ');
  }, [classFilter, classes, sectionFilter, sections, urgencyFilter]);

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;

    const worksheet = XLSX.utils.json_to_sheet(rows.map((row) => ({
      'Student Name': row.studentName,
      'Father Name': row.fatherName,
      Class: row.className,
      Section: row.sectionName,
      'Total Fee': row.totalFee,
      'Paid Amount': row.paidAmount,
      'Pending Amount': row.pendingAmount,
      'Due Date': row.dueDate,
      Urgency: row.urgency,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pending Fees');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `${exportBaseName}.xlsx`);
  };

  const handleExportPdf = () => {
    const rows = buildExportRows();
    if (!rows.length) return;

    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text('Pending Fees Report', 14, 16);
    doc.setFontSize(10);
    doc.text(exportContextLabel, 14, 22);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    doc.autoTable({
      startY: 34,
      head: [[
        'Student',
        'Father',
        'Class',
        'Section',
        'Total',
        'Paid',
        'Pending',
        'Due Date',
        'Urgency',
      ]],
      body: rows.map((row) => ([
        row.studentName,
        row.fatherName,
        row.className,
        row.sectionName,
        `${currencySymbol} ${formatCurrency(row.totalFee)}`,
        `${currencySymbol} ${formatCurrency(row.paidAmount)}`,
        `${currencySymbol} ${formatCurrency(row.pendingAmount)}`,
        row.dueDate,
        row.urgency,
      ])),
      styles: { fontSize: 8, cellPadding: 2.4 },
      headStyles: { fillColor: [220, 38, 38] },
    });

    doc.save(`${exportBaseName}.pdf`);
  };

  return (
    <ViewShell>
      <HeroStrip>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Typography variant="overline" sx={{ color: 'error.main', fontWeight: 800, letterSpacing: '0.12em' }}>
              Pending Fees
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800 }}>
              See overdue dues first and act without reading a huge table
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
              This view is designed for finance follow-up: who needs attention now, how much is pending, and what action to take next.
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={3}>
                <MetricTile>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Pending</Typography>
                      <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>{currencySymbol} {formatCurrency(totalPending)}</Typography>
                    </Box>
                    <CurrencyRupeeIcon color="error" />
                  </Stack>
                </MetricTile>
              </Grid>
              <Grid item xs={12} sm={3}>
                <MetricTile>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Students</Typography>
                      <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>{studentCount}</Typography>
                    </Box>
                    <PeopleAltOutlinedIcon color="primary" />
                  </Stack>
                </MetricTile>
              </Grid>
              <Grid item xs={12} sm={3}>
                <MetricTile>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Overdue</Typography>
                      <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>{overdueCount}</Typography>
                    </Box>
                    <WarningAmberIcon color="error" />
                  </Stack>
                </MetricTile>
              </Grid>
              <Grid item xs={12} sm={3}>
                <MetricTile>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Due Soon</Typography>
                      <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>{dueSoonCount}</Typography>
                    </Box>
                    <EventAvailableIcon color="warning" />
                  </Stack>
                </MetricTile>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </HeroStrip>

      <FilterBar>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Class</InputLabel>
              <Select value={classFilter} label="Class" onChange={(e) => { setClassFilter(e.target.value); setSectionFilter(''); }}>
                <MenuItem value="">All Classes</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.classId} value={String(cls.classId)}>{cls.className || 'Unnamed Class'}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" disabled={!classFilter}>
              <InputLabel>Section</InputLabel>
              <Select value={sectionFilter} label="Section" onChange={(e) => setSectionFilter(e.target.value)}>
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((sec) => (
                  <MenuItem key={sec.sectionId} value={String(sec.sectionId)}>{sec.sectionName || 'Unnamed Section'}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select value={urgencyFilter} label="Priority" onChange={(e) => setUrgencyFilter(e.target.value)}>
                <MenuItem value="all">All Pending</MenuItem>
                <MenuItem value="overdue">Overdue First</MenuItem>
                <MenuItem value="soon">Due Soon</MenuItem>
                <MenuItem value="pending">Upcoming</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', md: 'flex-end' }} flexWrap="wrap">
              <Button variant="outlined" startIcon={<GridOnIcon />} onClick={handleExportExcel} disabled={!filteredFees.length}>
                Excel
              </Button>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf} disabled={!filteredFees.length}>
                PDF
              </Button>
              <Button variant="outlined" onClick={fetchPendingFees}>Refresh</Button>
            </Stack>
          </Grid>
        </Grid>
      </FilterBar>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && <Alert severity="error">{error}</Alert>}

      {!loading && !error && filteredFees.length === 0 && (
        <Alert severity="info">No pending fees found for the current filters.</Alert>
      )}

      {!loading && !error && filteredFees.length > 0 && (
        <Stack spacing={1.4}>
          {filteredFees.map((fee) => (
            <FeeRow key={`${fee.studentId}-${fee.classId}-${fee.sectionId}`}>
              <Box p={2}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {`${fee.firstName || ''} ${fee.lastName || ''}`.trim() || 'Unnamed Student'}
                        </Typography>
                        <StatusChip label={fee.urgency.label} tone={fee.urgency.tone} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {fee.className || 'N/A'} / {fee.sectionName || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Father: {fee.fatherName || 'N/A'}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary">Pending Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: fee.urgency.tone === 'overdue' ? 'error.main' : 'text.primary' }}>
                      {currencySymbol} {formatCurrency(fee.pendingAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary">Paid / Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {currencySymbol} {formatCurrency(fee.paidAmount)} / {currencySymbol} {formatCurrency(fee.totalFee)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" color="text.secondary">Due Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {fee.dueDate || fee.lastDueDate || fee.nextDueDate || 'Not available'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ReceiptLongIcon />}
                        onClick={() => openTransactions(fee)}
                      >
                        Details
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </FeeRow>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Pending Fee Transactions</DialogTitle>
        <DialogContent dividers>
          {selectedFee?.feeTransactions?.length ? (
            <Stack spacing={1.2}>
              {selectedFee.feeTransactions.map((tx, index) => (
                <Box key={index}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {tx.paymentDate ? new Date(tx.paymentDate).toLocaleDateString() : 'No payment date'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Status: {tx.status || 'N/A'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {currencySymbol} {formatCurrency(tx.amountPaid)}
                    </Typography>
                  </Stack>
                  {index < selectedFee.feeTransactions.length - 1 && <Divider sx={{ mt: 1.2 }} />}
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>No transactions available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </ViewShell>
  );
};

export default PendingFeesComponent;
